import {useContext, useEffect} from 'react';
import {ActivityMaterial} from "../../types/ActivityMaterial";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {StringUtility} from "../common/StringUtility";

export const ActivityMaterialsListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.settings.pathSegment}/activity-materials`;
    const dataStore = useRenderableDataStore({
        activityMaterialsList: [],
        loading: true,
    });

    useEffect(function () {
        reloadList();
    }, []);

    function reloadList() {
        dataStore.updateAndRender({loading: true});
        ApiUtility.getActivityMaterials()
            .then(function (activityMaterialsList) {
                dataStore.updateAndRender({
                    activityMaterialsList: activityMaterialsList,
                    loading: false,
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the materials list.`);
            });
    }

    function goToAddActivityMaterialPage() {
        context.navigator.navigateTo(`${urlBase}/add`);
    }

    function goToUpdateActivityMaterialPage(activityMaterial: ActivityMaterial) {
        context.navigator.navigateTo(`${urlBase}/${activityMaterial.id}`);
    }

    function renderActivityMaterialsListDisplay(activityMaterialsList: ActivityMaterial[]) {
        let activityMaterialsListDisplay = [];

        for (let i = 0; i < activityMaterialsList.length; i++) {
            const activityMaterial = activityMaterialsList[i];
            const scopeNames = activityMaterial.activity_scopes.map((activityScope) => activityScope.name);
            const scopesSummary = scopeNames.length === 0 ? "No scopes" : scopeNames.join(", ");
            const deactivatedRowClass = activityMaterial.active ? '' : ' deactivated';
            const inactiveSuffix = activityMaterial.active ? '' : ' (Inactive)';

            activityMaterialsListDisplay.push(
                <div
                    key={i + 1}
                    className={`common-list-row activity-materials-list-row${deactivatedRowClass}`}
                    onClick={() => goToUpdateActivityMaterialPage(activityMaterial)}
                    data-activity-material-id={activityMaterial.id}
                >
                    <div className="common-list-row-content">
                        <div className="common-list-row-ticket-edge">
                            <div className="common-list-row-ticket-circle"/>
                        </div>
                        <div className="common-list-row-main">{activityMaterial.name}{inactiveSuffix}</div>
                        <div className="activity-materials-list-row-unit-cell">
                            <div className="activity-materials-list-row-unit-pill">
                                {StringUtility.pluralize(activityMaterial.measurement_unit)}
                            </div>
                        </div>
                        <div className="common-list-row-details">
                            <div className="activity-materials-list-row-scopes-cell" title={scopesSummary}>
                                {scopesSummary}
                            </div>
                        </div>
                        <div className="common-list-row-action-indicators">
                            <div className="activity-materials-list-row-edit-action-cell">EDIT</div>
                            <div className="common-list-row-pointer-cell">
                                <img
                                    src="/static/images/arrow-right-tiny-green.svg"
                                    className="common-list-row-pointer-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return activityMaterialsListDisplay;
    }

    return (
        <div className="common-list-page activity-materials-list-page">
            <div className="common-list-page-header">
                <div className="common-list-page-header-content">
                    <div className="common-list-page-header-content-left">
                        <div className="common-list-page-header-text">Materials</div>
                    </div>
                    <div className="common-list-page-header-content-right">
                        <div
                            className="icon-button add-button"
                            onClick={goToAddActivityMaterialPage}
                        >
                            <div className="icon-button-content">
                                <img
                                    className="icon-button-image"
                                    src="/static/images/plus-green.svg"
                                />
                                <div className="icon-button-text">Add Material</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="common-list-page-content">
                <ConditionalRenderer
                    if={dataStore.data().loading}
                    renderer={() => <OverlayLoadingIndicator/>}
                />
                {(() => {
                    if (dataStore.data().activityMaterialsList.length > 0) {
                        return (
                            <div className="common-list-content activity-materials-list-content">
                                {renderActivityMaterialsListDisplay(dataStore.data().activityMaterialsList)}
                            </div>
                        );
                    } else if (!dataStore.data().loading) {
                        return (
                            <div className="common-empty-list-message-container">
                                <div className="common-empty-list-message">No materials found.</div>
                            </div>
                        );
                    }
                })()}
            </div>
        </div>
    );
};
